import { useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useCreateEvent, useEventTypes } from '../hooks/useEvents'
import { useMe } from '../hooks/useMe'
import { ApiClient } from '../api'
import { ManagerPicker } from './ManagerPicker'
import { StationLocationField } from './StationLocationField'
import { MultiDatePicker } from './MultiDatePicker'
import {
  ModalShell, Field, inputClass, FormStatus, ServerError, CancelButton, PrimaryButton
} from './modal-form'
import type { EventCreate, EventType } from '../types'

// Centred modal for creating a new event. Visual pattern lives in
// modal-form.tsx (ModalShell + Field + inputClass). Status is server-defaulted
// to 'Draft' and progress to 0 - those fields aren't in the form. event_type
// IS required at creation (every event has a type from the moment it exists),
// unlike Edit where type changes are gated separately.
//
// On successful POST: seed the per-event cache (handled by useCreateEvent),
// close the modal, and navigate to /events/:newId so the user lands directly
// on their new event ready to keep editing.

interface Props {
  onClose: () => void
  // asIdea creates the event in the pre-Draft 'Idea' status and, on success,
  // opens it in Forecast Studio. The date picker becomes the "proposed slot".
  asIdea?: boolean
}

// Today's date in the user's local timezone, formatted YYYY-MM-DD. Local
// rather than UTC so a UK user creating an event at 22:00 doesn't get
// "tomorrow" in the picker.
function todayLocalIso(): string {
  const d = new Date()
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

export function NewEventModal({ onClose, asIdea }: Props) {
  const { data: types, isLoading, error } = useEventTypes()
  const title = asIdea ? 'New idea' : 'New event'

  // Loading / empty / error states render inside the shell so the modal
  // animation is consistent with the form variant. The form sub-component is
  // only mounted once we have types (so its useState can default to types[0]).
  if (isLoading) {
    return (
      <ModalShell
        title={title}
        onClose={onClose}
        body={<FormStatus>Loading event types…</FormStatus>}
      />
    )
  }
  if (error) {
    return (
      <ModalShell
        title={title}
        onClose={onClose}
        body={
          <FormStatus tone="error">
            Could not load event types: {(error as Error).message}
          </FormStatus>
        }
      />
    )
  }
  if (!types || types.length === 0) {
    return (
      <ModalShell
        title={title}
        onClose={onClose}
        body={
          <FormStatus tone="error">
            No event types are configured. Seed dbo.EventTypes before creating events.
          </FormStatus>
        }
      />
    )
  }
  return <NewEventForm types={types} onClose={onClose} asIdea={asIdea} />
}

// ---- inner form (mounted only once types are loaded) ----------------------

function NewEventForm({ types, onClose, asIdea }: { types: EventType[]; onClose: () => void; asIdea?: boolean }) {
  const navigate = useNavigate()
  const createEvent = useCreateEvent()

  const today = todayLocalIso()

  const [name, setName] = useState('')
  const [eventTypeId, setEventTypeId] = useState<number>(types[0].id)
  // Multi-date (item 7): the event's day set. start/end are derived as the
  // min/max envelope so the server-side range validation + filters keep working.
  const [dates, setDates] = useState<string[]>([today])
  const sortedDates = [...dates].sort()
  const startDate = sortedDates[0] ?? ''
  const endDate = sortedDates[sortedDates.length - 1] ?? ''
  const [location, setLocation] = useState('')
  const [capacity, setCapacity] = useState('')
  const [description, setDescription] = useState('')
  const [managerUserId, setManagerUserId] = useState<number | null>(null)
  const [meracEventCode, setMeracEventCode] = useState<string>('')   // '' = no binding

  // MERAC codes only load when the caller is admin — the endpoint is
  // admin-gated server-side; non-admins simply won't see the dropdown.
  const { data: me } = useMe()
  const { data: meracCodes } = useQuery({
    queryKey: ['merac-codes', { active: true }],
    queryFn: () => ApiClient.meracEventCodes({ active: 'true' }),
    enabled: !!me?.isAdmin,
    staleTime: 5 * 60_000   // catalog rarely changes; 5 minutes is fine
  })

  // Validation mirrors the server.
  const trimmedName = name.trim()
  const capacityNum = capacity === '' ? null : Number(capacity)

  const fieldErrors: { name?: string; dates?: string; capacity?: string } = {}
  if (trimmedName.length === 0) fieldErrors.name = 'Name is required'
  else if (trimmedName.length > 255) fieldErrors.name = 'Name must be 255 characters or fewer'
  if (dates.length === 0) fieldErrors.dates = 'Pick at least one date'
  if (capacity !== '' && (capacityNum === null || !Number.isInteger(capacityNum) || capacityNum < 0)) {
    fieldErrors.capacity = 'Capacity must be a whole number (or leave blank)'
  }

  const isValid = Object.keys(fieldErrors).length === 0
  const saving = createEvent.isPending

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!isValid || saving) return
    const body: EventCreate = {
      name: trimmedName,
      eventTypeId,
      startDate,
      endDate,
      dates: sortedDates,
      location: location.trim() === '' ? null : location.trim(),
      capacity: capacityNum,
      description: description.trim() === '' ? null : description,
      managerUserId,
      meracEventCode: asIdea ? null : (meracEventCode === '' ? null : meracEventCode),
      asIdea: asIdea || undefined
    }
    createEvent.mutate(body, {
      onSuccess: (created) => {
        onClose()
        navigate(asIdea ? `/forecast/${created.id}` : `/events/${created.id}`)
      }
    })
  }

  return (
    <ModalShell
      title={asIdea ? 'New idea' : 'New event'}
      onClose={onClose}
      onSubmit={handleSubmit}
      footer={
        <>
          <CancelButton onClick={onClose} disabled={saving} />
          <PrimaryButton disabled={!isValid || saving}>
            {saving ? 'Creating…' : asIdea ? 'Create idea & open Studio' : 'Create event'}
          </PrimaryButton>
        </>
      }
      body={
        <div className="space-y-4">
          <Field label="Event name" error={fieldErrors.name}>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={255}
              autoFocus
              placeholder="e.g. Diesel Gala"
              className={inputClass(!!fieldErrors.name)}
            />
          </Field>

          <Field label="Event type">
            <select
              value={eventTypeId}
              onChange={(e) => setEventTypeId(Number(e.target.value))}
              className={inputClass(false)}
            >
              {types.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </Field>

          <ManagerPicker
            value={managerUserId}
            onChange={setManagerUserId}
            disabled={saving}
          />

          {/* MERAC event-code binding. Admin-only (the catalog endpoint
              is admin-gated, so meracCodes is undefined for non-admins
              and the field doesn't render). Optional — events without
              a MERAC link still create cleanly. */}
          {!asIdea && me?.isAdmin && meracCodes != null && meracCodes.length > 0 && (
            <Field
              label="MERAC event code"
              hint="Binds this event to MERAC for ticket-revenue + timetable sync. Leave blank for events without MERAC sales."
            >
              <select
                value={meracEventCode}
                onChange={(e) => setMeracEventCode(e.target.value)}
                className={inputClass(false)}
                disabled={saving}
              >
                <option value="">— No MERAC binding —</option>
                {meracCodes.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.code} — {c.description}
                  </option>
                ))}
              </select>
            </Field>
          )}

          <Field
            label={asIdea ? 'Proposed slot' : 'Event dates'}
            error={fieldErrors.dates}
            hint={asIdea
              ? 'The candidate dates. Forecast Studio reads the running timetable on these days to work out the normal day the event would displace.'
              : 'Pick every day the event runs — they can be non-consecutive and span months.'}
          >
            <MultiDatePicker value={dates} onChange={setDates} disabled={saving} />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <StationLocationField value={location} onChange={setLocation} disabled={saving} />
            <Field label="Capacity" error={fieldErrors.capacity} hint="Leave blank for no cap">
              <input
                type="number"
                min="0"
                step="1"
                value={capacity}
                onChange={(e) => setCapacity(e.target.value)}
                placeholder="—"
                className={inputClass(!!fieldErrors.capacity)}
              />
            </Field>
          </div>

          <Field label="Description">
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className={`${inputClass(false)} resize-y font-body`}
              placeholder="Optional - what is the event, who's it for, what makes it different from last year's?"
            />
          </Field>

          <div className="text-xs text-ink-soft bg-parchment border border-line rounded px-3 py-2">
            {asIdea ? (
              <>New ideas start in <span className="font-semibold">Idea</span> status and open in Forecast Studio.
              They stay off the operating plan until you commit an approved forecast.</>
            ) : (
              <>New events start in <span className="font-semibold">Draft</span> status with 0% build progress.
              You can change those after creation from the event's Edit dialog.</>
            )}
          </div>

          {createEvent.isError && (
            <ServerError title={asIdea ? 'Could not create idea' : 'Could not create event'} error={createEvent.error as Error} />
          )}
        </div>
      }
    />
  )
}
